// Wire types for the AP REST API (`/api/v1`), as the app holds them.
//
// ONE CONTRACT. Every payload the server sends is `shared/src/dto.ts`'s type,
// imported here — the backend serialises from the same declarations, so the
// two sides cannot drift. Keys are the Postgres column names verbatim
// (`docs/api.md` §0.1), one vocabulary from the database to the React
// component.
//
// ONE DIFFERENCE, AND IT IS MECHANICAL — money. The wire carries money (and a
// line's quantity) as a decimal string ("1234.56") so no cent is ever lost to
// IEEE-754 drift. The app's arithmetic (the release gate, the line items
// footer, the GL Summary) works in numbers, so the HTTP client (`http.ts`)
// parses those fields into `number` on the way in and formats them back on the
// way out. `Numeric<T, K>` below is the only place that difference is written:
// a wire type with its decimal-string fields `K` read as numbers. A line's
// `unit_price` is NOT among them: it stays the wire's exact text (D-249).
//
// What remains hand-written here is what the server does not send: the
// request bodies and query parameters the app composes, and the Graph
// directory person (`DirectoryUser`), which is not an API payload at all.

import type * as W from '@ap/shared'
import type {
  AnalyticsDimension,
  AssignmentAction,
  DecisionOutcome,
  DecisionStep,
  EntityType,
  InvoiceStatus,
  ReasonCategory,
  SpendGrouping,
} from '@ap/shared'
import type { AssignmentRole, Workspace } from '../lib/constants'

/** A wire type with its decimal-string fields `K` read as numbers. */
type Numeric<T, K extends keyof T> = {
  [P in keyof T]: P extends K ? Exclude<T[P], string> | number : T[P]
}

// The database enums, as `@ap/shared` declares them: the stored strings, verbatim.
export type {
  AccountSource,
  AnalyticsDimension,
  AnalyticsSection,
  AssignmentAction,
  CompanyNameSource,
  CompanySource,
  DecisionSource,
  DepartmentSource,
  EntityType,
  LineItemDetailKind,
  OversightRuleField,
  OversightRuleOperator,
  OversightRuleScope,
  ReferenceSection,
  RequiredApprovals,
  ReportSort,
  RulesSection,
  SpendGrouping,
  ExportStatus as ExportStatusValue,
  UploadPhase as UploadPhaseValue,
  UploadStatus as UploadStatusValue,
  UserRole as UserRoleToken,
} from '@ap/shared'

export type Page<T> = W.Paginated<T>
export type Clearable<T> = W.Clearable<T>

// ---------------------------------------------------------------------------
// Session / profile (§1.1)
// ---------------------------------------------------------------------------

export type Profile = W.MeResponse
export type ProfileAssignment = W.MeAssignment
export type ProfileCapabilities = W.MeResponse['capabilities']

// ---------------------------------------------------------------------------
// Stuck tiers (§2.2)
// ---------------------------------------------------------------------------

export type StuckNodeCompany = W.StuckNodeCompany
export type StuckNode = W.StuckNode
export type QueueStuck = W.QueueStuckResponse

// ---------------------------------------------------------------------------
// Invoices and their bundle (§3–§9)
// ---------------------------------------------------------------------------

/**
 * The fields `http.ts` reads as numbers, and the types say so FROM THE SAME
 * LIST, so a field cannot be typed a number and arrive as text (round 2 wave
 * C5, D-289: `shipping` and `prior_balance` were typed numbers and parsed as
 * nothing, so the queue footer joined Shipping figures as strings).
 * `mock/tests/money-fields.test.ts` checks the lists against every
 * `MoneyString` field `shared/src/dto.ts` declares.
 */
export const INVOICE_NUMERIC_FIELDS = ['subtotal', 'tax', 'shipping', 'total', 'prior_balance'] as const
/** `quantity` is not money (`numeric(14,4)`), but it is read as a number too. */
export const LINE_ITEM_NUMERIC_FIELDS = ['quantity', 'total'] as const

export type Invoice = Numeric<W.Invoice, (typeof INVOICE_NUMERIC_FIELDS)[number]>
export type InvoiceLineItem = Numeric<W.InvoiceLineItem, (typeof LINE_ITEM_NUMERIC_FIELDS)[number]>
export type LineItemMarketingDetail = W.LineItemMarketingDetail
export type LineItemTransportationDetail = W.LineItemTransportationDetail
export type InvoiceInclude = W.InvoiceInclude
export type Flag = W.Flag
export type Decision = W.Decision
export type InvoiceUploadSummary = W.InvoiceUploadSummary
export type InvoiceNotificationSummary = W.InvoiceNotificationSummary
export type ActiveHold = W.ActiveHold
export type Hold = W.Hold
export type Correction = W.Correction
export type Conversation = W.Conversation
export type AssociatedEntity = W.AssociatedEntity

/** `GET /invoices/{id}`, its money read as numbers. */
export type InvoiceBundle = Omit<W.InvoiceDetailResponse, 'invoice' | 'line_items'> & {
  invoice: Invoice
  line_items?: InvoiceLineItem[]
}

// ---------------------------------------------------------------------------
// Uploads and exports (§10–§11)
// ---------------------------------------------------------------------------

export type Upload = W.Upload
export type ExportFile = W.ExportFile
export type ExportBatch = W.ExportBatch
export type PresignResult = W.UploadPresignResponse
export type SignedUrl = W.PdfUrlResponse
export type ExportResult = W.ExportCreateResponse
export type ExportDetail = W.ExportDetailResponse

// ---------------------------------------------------------------------------
// Assignments, roles, reminders (§12–§14)
// ---------------------------------------------------------------------------

export type Assignment = W.Assignment
export type AssignmentResult = W.AssignmentCreateResponse
export type UserRole = W.UserRoleGrant
export type ReminderSubscription = W.ReminderSubscription

// ---------------------------------------------------------------------------
// Reference (master) data (§15)
// ---------------------------------------------------------------------------

export type Company = W.Company
export type CompanyEmailDomain = W.CompanyEmailDomain
export type IntakeSender = W.IntakeSender
export type SenderFormatDefault = W.SenderFormatDefault
export type SenderRule = W.SenderRule
export type Vendor = W.Vendor
export type Code = W.Code
export type CodingRule = W.CodingRule
export type Department = W.Department
export type Account = W.Account
export type HalfOrigin = W.HalfOrigin
export type InactiveCascade = W.InactiveCascade
export type MarketingCategory = W.MarketingCategory
export type VendorAlias = W.VendorAlias
export type CompanyAlias = W.CompanyAlias
export type VendorExtractionGuidance = W.VendorExtractionGuidance
export type Vehicle = W.Vehicle
export type OversightRule = W.OversightRule
export type OversightRuleCondition = W.OversightRuleCondition
export type OversightRuleConditionWrite = W.OversightRuleConditionWrite
export type DecisionRoutingRule = W.DecisionRoutingRule
/** The Override pop-up's answer, and the Override as stored on the invoice (D-350). */
export type RouteOverrideWrite = W.RouteOverrideWrite
export type RouteOverride = W.RouteOverride
export type DecidedRoute = W.DecidedRoute
export type UploadNotificationRecipient = W.UploadNotificationRecipient
export type Notification = W.Notification
export type NotificationSummary = W.NotificationSummary
export type UploadNotificationRecipientCreate = W.UploadNotificationRecipientCreate

/** `PUT /sender-rules` — every row of one uploader rule in one transaction.
    The three versions say which Active row of each side the caller saw
    (`null`: none). The clauses — `key_kind`, `intake_allowed`, `company_id`
    and `document_form` (`null`: interpreted) — come together or not at all. */
export interface SenderRulePut {
  key: string
  is_active: boolean
  key_kind?: W.SenderKeyKind
  intake_allowed?: boolean
  company_id?: string | null
  document_form?: W.DocumentForm | null
  intake_sender_row_version: number | null
  company_email_domain_row_version: number | null
  sender_format_default_row_version: number | null
}

/** `vehicleIsActiveFor` (D-65) and the three `vehicles.status` values. The
    editor shows the consequence of a Status before it is saved. */
export { VEHICLE_STATUSES, vehicleIsActiveFor } from '@ap/shared'

// --- The fleet spreadsheet import (§15.20–15.21) ----------------------------

/** The nine fields the fleet spreadsheet carries, in the owner's column order. */
export const FLEET_FIELDS = [
  'number',
  'vin',
  'license_plate',
  'make',
  'model',
  'year',
  'company_label',
  'department',
  'status',
] as const satisfies readonly W.FleetField[]
export type FleetField = W.FleetField

export const FLEET_FIELD_LABEL: Record<FleetField, string> = {
  number: 'Veh #',
  vin: 'VIN',
  license_plate: 'License Plate',
  make: 'Make',
  model: 'Model',
  year: 'Year',
  company_label: 'Company',
  department: 'Department',
  status: 'Status',
}

export type VehicleImportPreview = W.VehicleImportPreviewResponse
export type VehicleImportResult = W.VehicleImportCommitResponse

// --- The Point Click Care vendor-list import (§15.22–15.23) -----------------
//
// Matched on `vendor_id` alone (never `vendor_name` — 95 real duplicate-name
// groups exist), and a blank cell LEAVES THE STORED VALUE ALONE rather than
// clearing it, the opposite of the fleet rule. See
// `backend/src/domain/vendorImport.ts`.

/** The importer's own field label, for the preview dialog. */
export const VENDOR_IMPORT_FIELD_LABEL: Record<W.VendorField, string> = {
  vendor_id: 'Vendor Code',
  vendor_name: 'Vendor Name',
  address_1: 'Address 1',
  address_2: 'Address 2',
  address_3: 'Address 3',
  city: 'City',
  state_or_province: 'State/Province',
  postal_code: 'Zip/Postal Code',
  phone: 'Office Phone',
  fax: 'Fax',
  // Point Click Care repeats the header names in its Remit To Different
  // Address block, so the preview names these by what they are (D-241).
  remittance_address_1: 'Remittance Address 1',
  remittance_address_2: 'Remittance Address 2',
  remittance_address_3: 'Remittance Address 3',
  remittance_city: 'Remittance City',
  remittance_state_or_province: 'Remittance State/Province',
  remittance_postal_code: 'Remittance Zip/Postal Code',
}

export type VendorImportPreview = W.VendorImportPreviewResponse
export type VendorFieldChange = VendorImportPreview['updates'][number]['changes'][number]
export type VendorImportResult = W.VendorImportCommitResponse

// ---------------------------------------------------------------------------
// Analytics (§16) — server-side aggregates, their money read as numbers
// ---------------------------------------------------------------------------

/** One aggregate row with its `total` read as a number. */
type Totalled<T extends { total: unknown }> = Numeric<T, 'total'>

export type AnalyticsEnvelope = W.AnalyticsEnvelope
export type AnalyticsKpiTile = Totalled<W.AnalyticsKpiTile>
export type AgeBandTile = Totalled<W.AnalyticsKpisResponse['outstanding_by_age'][number]>
export type AnalyticsDimensionRow = Totalled<W.AnalyticsDimensionRow>
export type AnalyticsCorrections = W.AnalyticsCorrectionsResponse

export type AnalyticsSpendByEntity = Omit<W.AnalyticsSpendByEntityResponse, 'entities'> & {
  entities: Totalled<W.AnalyticsSpendByEntityResponse['entities'][number]>[]
}

export type AnalyticsKpis = Omit<
  W.AnalyticsKpisResponse,
  'imported_this_week' | 'in_progress' | 'past_due' | 'exported_this_week' | 'outstanding_by_age' | 'outstanding_unknown_age'
> & {
  imported_this_week: AnalyticsKpiTile
  in_progress: AnalyticsKpiTile
  past_due: AnalyticsKpiTile
  exported_this_week: AnalyticsKpiTile
  outstanding_by_age: AgeBandTile[]
  outstanding_unknown_age: AnalyticsKpiTile
}

export type AnalyticsByDimension = Omit<W.AnalyticsByDimensionResponse, 'rows'> & {
  rows: AnalyticsDimensionRow[]
}

export type AnalyticsSpendByCode = Omit<W.AnalyticsSpendByCodeResponse, 'rows'> & {
  rows: Totalled<W.AnalyticsSpendByCodeResponse['rows'][number]>[]
}

/** The three dimensions §16.7 breaks down by, and how §16.8 rolls up spend. */
export { ANALYTICS_DIMENSIONS, SPEND_GROUPINGS } from '@ap/shared'

// ---------------------------------------------------------------------------
// Reports (§20) and ops (§18)
// ---------------------------------------------------------------------------

/** `REPORT_MAX_BYTES` is enforced HERE, as the browser assembles;
    `REPORT_INVOICE_CAP` server-side (a 422). */
export { REPORT_INVOICE_CAP, REPORT_MAX_BYTES, REPORT_SORTS } from '@ap/shared'
export type ReportCriteria = W.ReportCriteria
export type ReportCodeSubtotal = W.ReportCodeSubtotal
export type ReportInvoiceRow = W.ReportInvoiceRow
export type CreateReportResponse = W.CreateReportResponse
export type ReportRequestSummary = W.ReportRequestSummary
export type ReportContentsResponse = W.ReportContentsResponse

export type OpsErrorKind = W.OpsErrorKind
export type OpsErrorsWindow = W.OpsErrorsWindow
export type OpsErrorRow = W.OpsErrorRow
export type OpsErrorsResponse = W.OpsErrorsResponse
export type DeployReport = W.DeployReport

// --- View sessions — handling time (§21) -------------------------------------

export type ViewSessionInput = W.ViewSessionInput
export type ViewSessionsResult = W.CreateViewSessionsResponse

// ---------------------------------------------------------------------------
// Request bodies the app composes — the server reads these with its
// validators, so they have no DTO of their own
// ---------------------------------------------------------------------------

export type InvoiceHeaderWrite = W.InvoiceHeaderWrite
export type IntakeSaveRequest = W.IntakeSaveRequest
export type RoutingPreviewRequest = W.RoutingPreviewRequest
export type RoutingPreview = W.RoutingPreview
/** One line on the intake save (§3.3), Import Control's. */
export type InvoiceLineItemWrite = NonNullable<NonNullable<W.IntakeSaveRequest['line_items']>['upsert']>[number]

/** `POST /invoices/{id}/intake-save`, its money read as numbers. */
export type IntakeSaveResult = InvoiceBundle & Pick<W.IntakeSaveResponse, 'released_to' | 'corrections_written'>

export type DecisionRequest = W.DecisionRequest

/** `POST /decisions`, its invoice's money read as numbers. */
export type DecisionResult = Omit<W.DecisionResponse, 'invoice'> & { invoice: Invoice }

export interface DecisionBatchRequest {
  step: DecisionStep
  decision: DecisionOutcome
  reason?: string | null
  reason_category?: ReasonCategory | null
  invoice_ids: string[]
  /** Where the click was (D-331). Client-reported. */
  source?: W.DecisionSource | null
}

export type DecisionBatchResult = W.BatchDecisionResponse

export interface InvoiceListParams {
  status?: InvoiceStatus[]
  company_id?: string[]
  vendor_id?: string[]
  /** Stored vendor names, for a pick with no vendor id; OR'd with `vendor_id` (code review D-03). */
  vendor_name?: string[]
  date_from?: string
  date_to?: string
  /** Money as a number here; the client sends it at two decimals. */
  total_min?: number
  total_max?: number
  export_id?: string
  upload_id?: string
  manual_entry?: boolean
  unresolved_flags?: boolean
  flag_type?: string[]
  q?: string
  since?: string
  // The dashboard's Filters button (owner, 2026-09-14). Same seven the
  // aggregates take, so the listing under the panels matches the panels.
  uploaded_from?: string
  uploaded_to?: string
  code_id?: string[]
  department_id?: string[]
  account_id?: string[]
  flag_type_any?: string[]
  has_active_hold?: boolean
  sort?: W.InvoiceSort
  limit?: number
  cursor?: string
}

export interface AssignmentListParams {
  workspace?: Workspace
  role?: AssignmentRole
  company_id?: string
  active?: boolean
  user_id?: string
  limit?: number
  cursor?: string
}

export interface AssignmentRequest {
  workspace: Workspace
  action: AssignmentAction
  role: AssignmentRole
  company_id?: string | null
  user_id: string
  user_upn: string
  user_name?: string | null
  target_assignment_id?: string | null
}

export interface ReminderWrite {
  title?: string | null
  days_of_week: string[]
  time_of_day: string
  workspaces: string[]
  steps: string[]
}

export interface PresignRequest {
  file_name: string
  content_type: string
  byte_size: number
  expected_type?: string
  /**
   * The uploader's company pick (owner item 21, 2026-08-31) — one value for a
   * whole batch. Sent here so a bad or out-of-scope choice fails before the
   * bytes go up; the register call is what stores it.
   */
  selected_company_id?: string | null
}

export interface RegisterUploadRequest {
  upload_id: string
  /** Presign's key. The server takes the stored file name from its last segment (code review A-04). */
  object_key: string
  expected_type?: string
  /** @see PresignRequest.selected_company_id. Validated against the caller's own scope. */
  selected_company_id?: string | null
}

/** Columns 4 and 5 of the export as the controller picked them at Export
    Control (D-137, D-138). Sent as `fiscal_year` / `fiscal_period`. */
export interface FiscalPick {
  fiscalYear: number
  fiscalPeriod: number
}

export interface ReferenceListParams {
  q?: string
  active?: boolean
  /** Vendors only: just those with an errant primary or remittance ZIP (D-241). */
  errant_zip?: boolean
  limit?: number
  cursor?: string
}

export interface AnalyticsParams {
  company_id?: string[]
  vendor_id?: string[]
  entity_type?: EntityType
  /** Which breakdown §16.7 should group by. Required by that endpoint only. */
  dimension?: AnalyticsDimension
  /** Which coding field §16.8 should roll spend up by. That endpoint only. */
  group_by?: SpendGrouping
  // THE DASHBOARD'S FILTER BUTTON (owner, 2026-09-14) — the Reporting screen's
  // criteria, which the aggregates and `GET /invoices` both accept, so the
  // panels and the listing under them answer the same question.
  status?: InvoiceStatus[]
  date_from?: string
  date_to?: string
  uploaded_from?: string
  uploaded_to?: string
  total_min?: string
  total_max?: string
  code_id?: string[]
  department_id?: string[]
  account_id?: string[]
  flag_type?: string[]
  flag_type_any?: string[]
  has_active_hold?: boolean
}

/** A person from the directory — the SPA reads Microsoft Graph directly
    (api-contract §0.11), so this is not an API DTO. */
export interface DirectoryUser {
  id: string
  display_name: string
  upn: string
  mail?: string | null
  job_title?: string | null
  department?: string | null
}
